import { useBankAccountsController } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/BankAccounts/useBankAccountsController.ts';
import {
	resolveOpenEvent,
	toBankAccountEventTitle,
	toBankAccountTimelineEvents,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/BankAccounts/BankAccountDetails/components/bankAccountTimeline.utils.ts';
import { toBankAccountEventDiff } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/BankAccounts/BankAccountDetails/components/bankAccountEventDiff.utils.ts';
import { ReviewPanelTab } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/BankAccounts/BankAccountDetails/components/ReviewPanel.types.ts';
import {
	getMipInvestorBankAccountDetail,
	getMipInvestorBankAccountHistory,
	reviewMipInvestorBankAccount,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/BankAccounts/BankAccountDetails/BankAccountDetails.api.ts';
import { useMIPManageContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/MIPManageContext.ts';
import { useDetailsPagination } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/hooks/useDetailsPagination.ts';
import { useMipManageBreadcrumbsPrefix } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/utils/mipManageBreadcrumbs.utils.ts';
import { MIP_MANAGE_ROUTES } from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { mipManagePath } from '@dealstack/frontend/app/pages/Routing/routes.utils.ts';
import { useBreadcrumbs } from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs.tsx';
import { useRejectionState } from '@dealstack/frontend/shared/hooks/useRejectionState.ts';
import { AddBreadcrumbItem } from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs.types.ts';
import { getPersonBankAccounts } from '@dealstack/frontend/app/services/Person.service.ts';
import { getBankAccountFormatRules } from '@dealstack/frontend/shared/dataExchange/services/bankAccountFormatRule.service.ts';
import { getCountryNameByCode } from '@dealstack/shared/lib/countries/countriesData.ts';
import {
	useTranslationCapManage,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams.ts';
import { BankAccountReviewAction } from '@dealstack/shared/lib/generated';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

export const useBankAccountDetailsController = () => {
	const { dealId, mipId, bankAccountId } = useRequiredParams('dealId', 'mipId', 'bankAccountId');
	const { search } = useLocation();
	const tMipManage = useTranslationMipManage();
	const tCapManage = useTranslationCapManage();
	const { mipData, hasAdminRole } = useMIPManageContext();
	const queryClient = useQueryClient();

	const detailQuery = useQuery({
		queryKey: [API_PATHS.MIP_INVESTOR_BANK_ACCOUNT_ID_GET, bankAccountId],
		queryFn: () => getMipInvestorBankAccountDetail({ bankAccountId }),
	});

	// Programme-scoped, because the shared entity-audit route filters on Audit.dealId —
	// NULL for every programme account, which is what left this tab empty.
	const historyQuery = useQuery({
		queryKey: [API_PATHS.MIP_INVESTOR_BANK_ACCOUNT_ID_HISTORY_GET, bankAccountId],
		queryFn: async ({ signal }) =>
			(await getMipInvestorBankAccountHistory({ bankAccountId }, { abortSignal: signal }))
				.payload ?? [],
	});

	const personId = detailQuery.data?.payload?.personId ?? '';

	// The drawer edits the person-scoped BankAccountRes, not the review payload: only that
	// shape carries identifiers' bankAccountFormatRuleId, and without it the form falls back
	// to classic IBAN entry and silently drops the account's local format.
	const personAccountsQuery = useQuery({
		queryKey: [API_PATHS.PERSON_PERSONID_BANKACCOUNT_GET, personId],
		queryFn: () => getPersonBankAccounts(personId),
		enabled: !!personId && hasAdminRole,
	});

	const countryCode = detailQuery.data?.payload?.countryCode ?? '';

	// Identifier history records which entry format a field belongs to as a rule id; the
	// country's catalogue is what turns it into a name a reviewer can read.
	const formatRulesQuery = useQuery({
		queryKey: [API_PATHS.BANK_ACCOUNT_FORMAT_RULES_COUNTRYCODE_GET, countryCode],
		queryFn: () => getBankAccountFormatRules(countryCode),
		enabled: !!countryCode,
	});

	const bankAccountFormatRulesSelectOptions = useMemo(
		() =>
			(formatRulesQuery.data ?? []).map((rule) => ({
				value: rule.id,
				label: `${getCountryNameByCode(rule.countryCode) ?? rule.countryCode} · ${rule.formatName}`,
			})),
		[formatRulesQuery.data]
	);

	// Rebuilds the list request from the URL params the row click carried over, so
	// prev/next walks the page the reviewer came from (and hits the same cache entry).
	const { bankAccounts: pageBankAccounts } = useBankAccountsController();

	const bankAccountIds = useMemo(
		() => pageBankAccounts.map((account) => account.id),
		[pageBankAccounts]
	);

	const getBankAccountPath = useCallback(
		(id: string) =>
			mipManagePath(MIP_MANAGE_ROUTES.BANK_ACCOUNT_DETAILS, {
				dealId,
				mipId,
				bankAccountId: id,
			}),
		[dealId, mipId]
	);

	const pagination = useDetailsPagination({
		ids: bankAccountIds,
		currentId: bankAccountId,
		getPath: getBankAccountPath,
	});

	const bankAccountsListPath = `${mipManagePath(MIP_MANAGE_ROUTES.BANK_ACCOUNTS, { dealId, mipId })}${search}`;

	const bankAccount = detailQuery.data?.payload;

	const breadcrumbsPrefix = useMipManageBreadcrumbsPrefix({
		dealId,
		mipId,
		programmeName: mipData?.programmeName ?? '',
	});

	const breadcrumbs = useMemo<AddBreadcrumbItem[]>(
		() => [
			...breadcrumbsPrefix,
			{
				name: tMipManage('tabs.bank_accounts'),
				url: bankAccountsListPath,
				hideInAppHeader: true,
			},
			{
				name: bankAccount?.ownerName ?? '',
				url: '',
				noLink: true,
				hideInAppHeader: true,
			},
		],
		[breadcrumbsPrefix, bankAccountsListPath, tMipManage, bankAccount?.ownerName]
	);

	useBreadcrumbs(breadcrumbs);

	const timelineEvents = useMemo(
		() => toBankAccountTimelineEvents(historyQuery.data ?? []),
		[historyQuery.data]
	);

	const [activeTab, setActiveTab] = useState(ReviewPanelTab.REVIEW);
	const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
	const selectedEvent = resolveOpenEvent(
		timelineEvents,
		selectedEventId,
		activeTab === ReviewPanelTab.HISTORY
	);

	const selectEvent = useCallback((eventId: string) => setSelectedEventId(eventId), []);

	const selectedEventTitle = selectedEvent
		? toBankAccountEventTitle(selectedEvent, tMipManage)
		: '';

	// The shared audit table names its fields from cap-manage, wherever it is rendered.
	const selectedEventDiff = useMemo(
		() =>
			selectedEvent
				? toBankAccountEventDiff(selectedEvent, {
						tCapManage,
						bankAccountFormatRulesSelectOptions,
					})
				: [],
		[selectedEvent, tCapManage, bankAccountFormatRulesSelectOptions]
	);

	const [isEditOpen, setIsEditOpen] = useState(false);

	// A programme account has no owning person, so the person-scoped drawer cannot edit it.
	const canEdit = hasAdminRole && !!personId;

	const editableBankAccount =
		personAccountsQuery.data?.find((account) => account.id === bankAccountId) ?? null;

	// An edit can move the account's status, so the queue and the timeline both go stale.
	const refreshAfterEdit = useCallback(async () => {
		await Promise.all([
			detailQuery.refetch(),
			historyQuery.refetch(),
			personAccountsQuery.refetch(),
			queryClient.invalidateQueries({
				queryKey: [API_PATHS.MIP_INVESTOR_BANK_ACCOUNT_LIST_POST],
			}),
		]);
	}, [detailQuery, historyQuery, personAccountsQuery, queryClient]);

	const { mutate: submitReview, isPending: isReviewPending } = useMutation({
		mutationFn: (variables: { action: BankAccountReviewAction; reason?: string }) =>
			reviewMipInvestorBankAccount({ bankAccountId, body: variables }),
		onSuccess: async () => {
			// A decision moves the list's counts and the history tab.
			await Promise.all([
				detailQuery.refetch(),
				historyQuery.refetch(),
				queryClient.invalidateQueries({
					queryKey: [API_PATHS.MIP_INVESTOR_BANK_ACCOUNT_LIST_POST],
				}),
			]);
			showToastV2({ message: tMipManage('bank_accounts.review.saved') });
		},
	});

	// The same decline-with-a-comment flow the KYC reviewer follows: the decline button
	// reveals the reason box, then confirms it.
	const decline = useRejectionState({
		emptyCommentError: tMipManage('bank_accounts.review.decline_reason.required'),
		// Prev/next walks the queue without remounting this page, so the reason has to be
		// tied to the account it was typed for.
		resetKey: bankAccountId,
		onConfirm: async (reason) =>
			submitReview({ action: BankAccountReviewAction.DECLINE, reason }),
	});

	// Approve and revoke apply straight away; only a decline collects a reason first.
	const reviewBankAccount = useCallback(
		async (action: BankAccountReviewAction) => {
			if (action === BankAccountReviewAction.DECLINE) {
				await decline.handleDecline();
				return;
			}

			// An abandoned reason must not survive a decision taken on this same account,
			// or revoking it would reopen the box still holding the old text.
			decline.reset();
			submitReview({ action });
		},
		[submitReview, decline]
	);

	return {
		dealId,
		mipId,
		bankAccountId,
		bankAccount,
		isLoading: detailQuery.isLoading,
		isError: detailQuery.isError,
		timelineEvents,
		isHistoryLoading: historyQuery.isLoading,
		activeTab,
		setActiveTab,
		selectedEvent,
		selectedEventTitle,
		selectedEventDiff,
		selectEvent,
		pagination,
		reviewBankAccount,
		isReviewPending,
		decline,
		canEdit,
		editableBankAccount,
		personId,
		isEditOpen,
		openEdit: () => setIsEditOpen(true),
		closeEdit: () => setIsEditOpen(false),
		refreshAfterEdit,
	};
};
